import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Button, Icon } from '@tviklax/ui-components';
import type { CarrierQuote } from './SchedulingContextTypes';

export interface CarrierQuotationsProps {
  quotes: CarrierQuote[];
  selectedId?: string;
  canSelect?: boolean;
  promisedDate?: string;
  onSelect?: (quote: CarrierQuote | null) => void;
  onViewBreakup?: (quote: CarrierQuote) => void;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Card = styled.div<{ $selected: boolean }>`
  border: 1px solid ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.gray200)};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ $selected, theme }) => ($selected ? theme.shadows.md : 'none')};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray700};
`;

const CarrierName = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray900};
`;

const CarrierLogo = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.gray200};
`;

const RecommendedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.successLight};
  color: ${({ theme }) => theme.colors.successDark};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  position: relative;
  cursor: help;

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    background: ${({ theme }) => theme.colors.gray900};
    color: ${({ theme }) => theme.colors.surface};
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 400;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
    z-index: 10;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const RiskIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.errorLight};
  border-left: 3px solid ${({ theme }) => theme.colors.error};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.errorDark};
  font-weight: 500;
  margin-top: 4px;
`;

const IndicativeBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray500};
  background: ${({ theme }) => theme.colors.gray100};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: 6px;
  vertical-align: middle;
`;

const calculateDaysVsPromised = (etaDays: number, promisedDate?: string): number | null => {
  if (!promisedDate) return null;
  const promised = new Date(promisedDate);
  const eta = new Date(Date.now() + etaDays * 24 * 60 * 60 * 1000);
  const diffDays = Math.ceil((eta.getTime() - promised.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : null;
};

export const CarrierQuotations: React.FC<CarrierQuotationsProps> = ({ quotes, selectedId, canSelect = true, promisedDate, onSelect, onViewBreakup }) => {
  const theme = useTheme();
  return (
    <Grid>
      {quotes.map((q, idx) => {
        const isRecommended = idx === 0; // First quote after sorting is recommended
        const daysLate = calculateDaysVsPromised(q.etaDays, promisedDate);
        return (
          <Card key={q.id} $selected={selectedId === q.id}>
            <Row>
              <CarrierName>
                <CarrierLogo aria-hidden />
                {q.carrier}
                {q.liveSchedule === false && <IndicativeBadge title="No live schedule available — pricing is indicative">Indicative</IndicativeBadge>}
              </CarrierName>
              <div style={{ fontWeight: 600, color: theme.colors.gray900 }}>${q.cost.toLocaleString()}</div>
            </Row>
            {isRecommended && (
              <RecommendedBadge data-tooltip="Best balance of cost and ETA for promised date">
                <Icon name="CheckCircle" size={11} /> Recommended
              </RecommendedBadge>
            )}
            <Row>
              <span>ETA</span>
              <span>{q.etaDays} days</span>
            </Row>
            <Row>
              <span>Transit</span>
              <span>{q.transit}</span>
            </Row>
            <Row>
              <span>Free Demurrage</span>
              <span>{q.freeDemurrageDays} days</span>
            </Row>
            {daysLate !== null && daysLate > 0 && (
              <RiskIndicator>
                <Icon name="AlertTriangle" size={13} /> Misses promised date by {daysLate} day{daysLate !== 1 ? 's' : ''}
              </RiskIndicator>
            )}
            <Row>
              <Button size="sm" variant="outline" onClick={() => onViewBreakup?.(q)}>View breakup</Button>
              <Button
                size="sm"
                variant={selectedId === q.id ? undefined : 'ghost'}
                disabled={!canSelect}
                onClick={() => onSelect?.(selectedId === q.id ? null : q)}
              >
                {selectedId === q.id ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="CheckCircle" size={13} /> Selected
                  </span>
                ) : (
                  'Select & Book'
                )}
              </Button>
            </Row>
          </Card>
        );
      })}
    </Grid>
  );
};
